import React, { FC, ReactNode } from 'react';
import { Box, Link, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getBadge } from '../constants/storeItems';

interface UserNameProps {
  username: string;
  /**
   * Kept per call site rather than unified. The app currently writes `@name`,
   * `u/name` and bare `name` in different places; normalising them is a visible
   * product change and does not belong inside a mechanical refactor.
   */
  prefix?: '@' | 'u/' | 'none';
  /**
   * Badge snapshotted on the post being rendered — NOT the author's current
   * badge. Passing the live one would silently rewrite history on old posts.
   */
  badgeKey?: string | null;
  link?: boolean;
  /** Wrap the name (not the badge) so hover previews keep their existing target. */
  wrap?: (children: ReactNode) => ReactNode;
  sx?: Record<string, unknown>;
}

/**
 * A username, optionally followed by the badge the post was written with.
 *
 * Exists for the same reason as UserAvatar: 16 sites rendered a username by
 * hand, so anything that decorates one had to be applied 16 times.
 */
const UserName: FC<UserNameProps> = ({
  username,
  prefix = 'none',
  badgeKey,
  link = false,
  wrap,
  sx = {},
}) => {
  const badge = getBadge(badgeKey);
  const label = prefix === 'none' ? username : `${prefix}${username}`;

  const name = link ? (
    <Link component={RouterLink} to={`/u/${username}`} sx={sx}>
      {label}
    </Link>
  ) : (
    <Box component="span" sx={sx}>
      {label}
    </Box>
  );

  const wrapped = wrap ? wrap(name) : name;

  // An unknown key renders as nothing rather than breaking the line — a badge
  // retired or unknown to this build should degrade, not crash.
  if (!badge) return <>{wrapped}</>;

  return (
    <>
      {wrapped}
      <Tooltip title={badgeKey as string}>
        <Box
          component="span"
          aria-label={`Badge: ${badgeKey}`}
          sx={{ ml: 0.4, fontSize: '0.95em', lineHeight: 1, verticalAlign: 'baseline' }}
        >
          {badge.glyph}
        </Box>
      </Tooltip>
    </>
  );
};

export default UserName;
